import React, { useRef } from "react";
import { useConfirm } from "../../../context/ConfirmContext";
import { toast } from "react-hot-toast";
import { FileText, ClipboardList } from "lucide-react";

// Imports des composants décomposés
import { Modal } from "../../ui/Modal";
import { ConsultationForm } from "../CurrentConsultationStepComponents/ConsultationForm";
import { AddedItemsSection } from "../CurrentConsultationStepComponents/AddedItemsSection";
import { AnalyseModal } from "../CurrentConsultationStepComponents/AnalyseModal";
import { ActionButtons } from "../CurrentConsultationStepComponents/ActionButtons";

// Imports existants
import MedicalPrescriptionInterface from "../MedicalPrescriptionInterface";
import AnalysesView from "../../documents/AnalysesView";
import PrescriptionView from "../../documents/PrescriptionView";
import GenerateDocumentButtons from "../../documents/GenerateDocumentButtons";

// Hooks personnalisés
import { useUserProfile } from "../../../hooks/useUserProfile";
import { generateOrderNumber } from "../../../utils/medication";
import { useState } from "react";

// Types
import type { Analyse } from "../../../types/analyse";
import { ModalHeader } from "../../ui/ModalHeader";
import { usePDFGeneration } from "../../../hooks/usePDFGeneration";
import { useModalsManager } from "../../../hooks/useModalsManager";
import { useAnalysesManager } from "../../../hooks/useAnalysesManager";

import { useConsultationWizardState } from "../../../hooks/useConsultationWizardState";

interface CurrentConsultationStepProps {
  doctorInfo?: {
    firstName: string;
    lastName: string;
    title: string;
  };
  prescriptionNumber?: string;
}

const CurrentConsultationStep: React.FC<CurrentConsultationStepProps> = ({
  doctorInfo: propDoctorInfo = {
    firstName: "",
    lastName: "",
    title: "Dr",
  },
  prescriptionNumber,
}) => {
  const {
    formData,
    updateFormData,
    analyses,
    medications,
    setAnalyses,
    setMedications,
    handleAddOrEditMedication,
  } = useConsultationWizardState();
  // Refs pour les documents
  const analysesDocumentRef = useRef<HTMLDivElement>(null);
  const prescriptionDocumentRef = useRef<HTMLDivElement>(null);

  // Hooks personnalisés
  const { userData, loading, error } = useUserProfile();
  const { handleDownloadPDF, handlePrint } = usePDFGeneration();
  const modalsManager = useModalsManager();
  const analysesManager = useAnalysesManager(analyses, setAnalyses);
  const { confirm } = useConfirm();

  // Configuration du docteur
  const doctorInfo: { firstName: string; lastName: string; title: string; specialty?: string } =
    loading || error || !userData
      ? propDoctorInfo
      : {
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        title: "Dr",
        specialty: userData?.specialty || "Médecin Généraliste",
      };

  // Gestionnaire pour les médicaments
  const handleDeleteMedication = async (index: number) => {
    const isConfirmed = await confirm(
      "Êtes-vous sûr de vouloir supprimer ce médicament ?",
      {
        title: "Supprimer le médicament",
        confirmText: "Supprimer",
        cancelText: "Annuler",
        isDangerous: true,
      }
    );

    if (isConfirmed) {
      const updatedMedications = medications.filter((_, i) => i !== index);
      setMedications(updatedMedications);
      toast.success("Médicament supprimé");
    }
  };

  // Gestionnaires combinés pour les analyses
  const handleAddAnalyse = () => {
    analysesManager.handleAddAnalyse();
    modalsManager.openAnalyseModal();
  };

  const handleEditAnalyse = (index: number) => {
    analysesManager.handleEditAnalyse(index);
    modalsManager.openAnalyseModal();
  };

  const handleAnalyseSubmit = () => {
    const success = analysesManager.handleAnalyseSubmit();
    if (success) {
      modalsManager.closeAnalyseModal();
    }
  };

  // Handler pour la soumission multiple d'analyses
  const handleAnalysesSubmitMultiple = (newAnalyses: Analyse[]) => {
    const success = analysesManager.handleAnalysesSubmitMultiple(newAnalyses);
    if (success) {
      modalsManager.closeAnalyseModal();
    }
  };

  const handleCloseAnalyseModal = () => {
    analysesManager.handleCloseAnalyseModal();
    modalsManager.closeAnalyseModal();
  };

  // Données patient pour les documents



  const patientInfo = {
    nom: formData.patientInfo.nom,
    prenom: formData.patientInfo.prenom,
    dateNaissance: formData.patientInfo.dateNaissance,
    adresse: formData.patientInfo.adresse,
  };


  const consultationDate = formData.consultationActuelle.date;

  // Temp order number when consultation hasn't been saved
  const [tempOrderNumber, setTempOrderNumber] = useState<string | null>(null);

  const handleOpenPrescriptionView = () => {
    const order = prescriptionNumber || generateOrderNumber();
    setTempOrderNumber(order);
    modalsManager.openPrescriptionModalView();
  };

  return (
    <div className="space-y-8">
      {/* Formulaire de consultation */}
      <ConsultationForm
        consultationData={formData.consultationActuelle}
        onUpdate={(field: string, value: string | boolean) =>
          updateFormData("consultationActuelle", field, value)
        }
      />

      {/* Section des éléments ajoutés */}
      <AddedItemsSection
        analyses={analyses}
        medications={medications}
        onAddAnalyse={handleAddAnalyse}
        onEditAnalyse={handleEditAnalyse}
        onDeleteAnalyse={analysesManager.handleDeleteAnalyse}
        onAddMedication={modalsManager.openPrescriptionModal}
        onDeleteMedication={handleDeleteMedication}
      />

      {/* Boutons d'action */}
      <ActionButtons
        onAddAnalyse={handleAddAnalyse}
        onAddPrescription={modalsManager.openPrescriptionModal}
      />

      {/* Boutons de génération de documents */}
      <GenerateDocumentButtons
        hasAnalyses={analyses.length > 0}
        hasMedications={medications.length > 0}
        onGenerateAnalyses={modalsManager.openAnalysesModal}
        onGeneratePrescription={handleOpenPrescriptionView}
        prescriptionNumber={prescriptionNumber}
      />

      {/* Modal pour l'ajout/modification d'analyse */}
      <AnalyseModal
        isOpen={modalsManager.showAnalyseModal}
        onClose={handleCloseAnalyseModal}
        analyse={analysesManager.analyseForm}
        onChange={analysesManager.setAnalyseForm}
        onSubmit={handleAnalyseSubmit}
        onSubmitMultiple={handleAnalysesSubmitMultiple}
        isEditing={analysesManager.editAnalyseIndex !== null}
      />

      {/* Modal pour l'ajout de prescription */}
      <Modal
        isOpen={modalsManager.showPrescriptionModal}
        onClose={modalsManager.closePrescriptionModal}
      >
        <MedicalPrescriptionInterface
          onClose={modalsManager.closePrescriptionModal}
          onMedicationsChange={setMedications}
          onAddOrEditMedication={handleAddOrEditMedication}
          medications={medications}
        />
      </Modal>

      {/* Modal pour la vue des analyses */}
      <Modal
        isOpen={modalsManager.showAnalysesModal}
        onClose={modalsManager.closeAnalysesModal}
      >
        <div className="p-8">
          <ModalHeader
            title="Demande d'Analyses"
            icon={ClipboardList}
            iconColor="text-blue-600"
            onClose={modalsManager.closeAnalysesModal}
          />
          <AnalysesView
            ref={analysesDocumentRef}
            analyses={analyses}
            patientInfo={patientInfo}
            doctorInfo={doctorInfo}
            consultationDate={consultationDate}
            instructions={analyses
              .filter(a => a.conseils && a.conseils.trim())
              .map(a => a.conseils)
              .join('\n') || undefined
            }
            onBack={() => modalsManager.handleBackFromDocument("analyses")}
            onPrint={() => handlePrint("analyses", analysesDocumentRef)}
            onDownloadPDF={() =>
              handleDownloadPDF(
                "analyses",
                analysesDocumentRef,
                formData.patientInfo.nom
              )
            }
            isDownloading={false}
          />
        </div>
      </Modal>

      {/* Modal pour la vue de prescription */}
      <Modal
        isOpen={modalsManager.showPrescriptionModalView}
        onClose={modalsManager.closePrescriptionModalView}
      >
        <div className="p-8">
          <ModalHeader
            title="Ordonnance Médicale"
            icon={FileText}
            iconColor="text-green-600"
            onClose={modalsManager.closePrescriptionModalView}
          />
          <PrescriptionView
            ref={prescriptionDocumentRef}
            medications={medications}
            patientInfo={patientInfo}
            doctorInfo={doctorInfo}
            consultationDate={consultationDate}
            orderNumber={tempOrderNumber ?? prescriptionNumber}
            instructions={formData.consultationActuelle.recommandations}
            onBack={() => modalsManager.handleBackFromDocument("prescription")}
            onPrint={() => handlePrint("prescription", prescriptionDocumentRef)}
            onDownloadPDF={() =>
              handleDownloadPDF(
                "prescription",
                prescriptionDocumentRef,
                formData.patientInfo.nom
              )
            }
            isDownloading={false}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CurrentConsultationStep;
